import { useMemo } from 'react';

import { BUSINESS_TOOL_SECTIONS, BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';

import { concatWithDot } from 'helpers/string';

import {
  historyAboutCompanyInitialValues,
  IHistoryAboutCompanyForm,
} from '../Sections/AboutCompany/CompanyHistory/utils/form';
import {
  generalAboutCompanyInitialValues,
  IGeneralAboutCompanyForm,
} from '../Sections/AboutCompany/GeneralInfo/utils/form';
import {
  aboutCompanyMissionInitialValues,
  IAboutCompanyMissionForm,
} from '../Sections/AboutCompany/MissionAndVision/utils/form';
import { ISwotAboutCompanyForm, swotAboutCompanyInitialValues } from '../Sections/AboutCompany/SWOT/utils/form';
import { equipmentInitialValues, IEquipment } from '../Sections/BusinessOperations/Equipment/utils/form';
import {
  ILocationsAndPremises,
  locationsAndPremisesInitialValue,
} from '../Sections/BusinessOperations/LocationAndPremises/utils/form';
import { assetsInitialValues, IAssets } from '../Sections/BusinessOperations/OtherAssets/utils/form';
import { IPartners, partnersInitialValues } from '../Sections/BusinessOperations/PartnersSection/utils/form';
import { IProduction, productionInitialValues } from '../Sections/BusinessOperations/ProductionSection/utils/form';
import { IQuality, qualityInitialValues } from '../Sections/BusinessOperations/QualityControl/utils/form';
import {
  ITechnologicalProcess,
  technologicalProcessInitialValues,
} from '../Sections/BusinessOperations/TechnologicalProcess/utils/form';
import {
  IWorkingRecourses,
  workingRecoursesInitialValues,
} from '../Sections/BusinessOperations/WorkingResources/utils/form';
import {
  companyDescriptionExecutiveSummaryInitialValues,
  ICompanyDescriptionExecutiveSummaryForm,
} from '../Sections/ExecutiveSummary/CompanyDescriptionExecSummary/utils/form';
import {
  financialInfoExecutiveSummaryInitialValues,
  IFinancialInfoExecutiveSummaryForm,
} from '../Sections/ExecutiveSummary/FinancialInformationExecSummary/utils/form';
import {
  financingNeedsExecutiveSummaryInitialValues,
  IFinancingNeedsExecutiveSummaryForm,
} from '../Sections/ExecutiveSummary/FinancialNeedsExecSummary/utils/form';
import {
  goalsAndObjectivesExecutiveSummaryInitialValues,
  IGoalsAndObjectivesExecutiveSummaryForm,
} from '../Sections/ExecutiveSummary/GoalsExecSummary/utils/form';
import {
  IMarketAndCustomersExecutiveSummaryForm,
  marketAndCustomersExecutiveSummaryInitialValues,
} from '../Sections/ExecutiveSummary/MarketExecSummary/utils/form';
import {
  IProductsOrServicesExecutiveSummaryForm,
  productsOrServicesExecutiveSummaryInitialValues,
} from '../Sections/ExecutiveSummary/ProductsExecSummary/utils/form';
import {
  IRisksExecutiveSummaryForm,
  risksExecutiveSummaryInitialValues,
} from '../Sections/ExecutiveSummary/RisksExecSummary/utils/form';
import {
  IOrganizationAndTeamExecutiveSummaryForm,
  organizationAndTeamExecutiveSummaryInitialValues,
} from '../Sections/ExecutiveSummary/TeamExecSummary/utils/form';
import {
  cashFlowElementsInitialValues,
  ICashFlowElements,
} from '../Sections/FinancialPlan/CashFlowElements/utils/form';
import {
  financialPerformanceInitialValues,
  IFinancialPerformance,
} from '../Sections/FinancialPlan/FinancialPerformance/utils/form';
import { IKeyAssumptions, keyAssumptionsInitialValues } from '../Sections/FinancialPlan/KeyAssumptions/utils/form';
import {
  IOpeningBalanceSheets,
  openingBalanceSheetsInitialValues,
} from '../Sections/FinancialPlan/OpeningBalanceSheets/utils/form';
import {
  IPlannedExpenses,
  plannedExpensesInitialValues,
  useGetPlannedExpensesInitialValues,
} from '../Sections/FinancialPlan/PlannedExpenses/utils/form';
import {
  IProfitAndLossProjections,
  profitAndLossProjectionInitialValues,
} from '../Sections/FinancialPlan/ProfitAndLossProjection/utils/form';
import {
  IProjectedBalanceSheets,
  projectedBalanceSheetsInitialValues,
} from '../Sections/FinancialPlan/ProjectedBalanceSheets/utils/form';
import {
  IProjectedCashFlow,
  projectedCashFlowInitialValues,
} from '../Sections/FinancialPlan/ProjectedCashFlow/utils/form';
import { IRevenuePlan, revenuePlanInitialValues } from '../Sections/FinancialPlan/RevenuePlan/utils/form';
import { globalInitialValues, IGlobalSection, useGetGlobalInitialValues } from '../Sections/Global/form';
import { IImplementationPlan, implementationPlanValues } from '../Sections/ImplementationPlan/utils/form';
import {
  ILegalFrameworkAndRisks,
  legalFrameworkAndRisksInitialValues,
} from '../Sections/LegalFrameworkAndRisks/utils/form';
import {
  externalPartnersAndAndAdvisersInitialValues,
  IExternalPartnersAndAndAdvisers,
} from '../Sections/ManagementAndOrganization/ExternalPartners/utils/form';
import {
  IManagementAndOrganization,
  managementAndOrganizationInitialValues,
} from '../Sections/ManagementAndOrganization/ManagementTeam/utils/form';
import {
  IStaffOrganization,
  staffOrganizationInitialValues,
} from '../Sections/ManagementAndOrganization/StaffOrganization/utils/form';
import {
  competitiveAnalysisInitialValues,
  ICompetitiveAnalysis,
} from '../Sections/MarketAnalysis/CompetitiveAnalysis/utils/form';
import { consumerNeedsInitialValues, IConsumerNeeds } from '../Sections/MarketAnalysis/ConsumerNeeds/utils/form';
import {
  IMarketAnalysisOverviewForm,
  marketAnalysisOverviewInitialValues,
} from '../Sections/MarketAnalysis/MarketAnalysisOverview/utils/form';
import { IMarketing, marketingInitialValues } from '../Sections/MarketingAndSales/MarketingPlan/utils/form';
import { ISales, salesInitialValues } from '../Sections/MarketingAndSales/SalesPlanSection/utils/form';
import { ICompanyOverviewForm, overviewCompanyInitialValues } from '../Sections/Overview/CompanyOverview/utils/form';
import {
  IFinancialInfoOverviewForm,
  overviewFinancialInfoInitialValues,
} from '../Sections/Overview/FinancialInformationOverview/utils/form';
import {
  IFundingNeedsOverviewForm,
  overviewFundingNeedsInitialValues,
} from '../Sections/Overview/FundingNeedsOverview/utils/form';
import {
  IMarketAndCustomersOverviewForm,
  overviewMarketAndCustomersInitialValues,
} from '../Sections/Overview/MarketOverview/utils/form';
import { IProjectOverviewForm, overviewProjectInitialValues } from '../Sections/Overview/ProjectOverview/utils/form';
import {
  IProductAndServicesAdvantagesForm,
  productAndServicesAdvantagesInitialValues,
} from '../Sections/ProductsAndServices/ProductAndServicesAdvantages/utils/form';
import {
  IProductAndServicesDeliveryAndSupportForm,
  productAndServicesDeliveryAndSupportInitialValues,
} from '../Sections/ProductsAndServices/ProductAndServicesDeliveryAndSupport/utils/form';
import {
  IProductAndServicesDescriptionForm,
  productAndServicesDescriptionInitialValues,
} from '../Sections/ProductsAndServices/ProductAndServicesDescription/utils/form';
import {
  IProductAndServicesRAndDForm,
  productAndServicesRAndDInitialValues,
} from '../Sections/ProductsAndServices/ProductAndServicesRAndD/utils/form';
import {
  IRaisingAndAllocatingFunds,
  raisingAndAllocatingFundsInitialValues,
} from '../Sections/RaisingAndAllocatingFunds/utils/form';

export type IBusinessToolForm = {
  [BUSINESS_TOOL_SECTIONS.GLOBAL]: IGlobalSection;
  [BUSINESS_TOOL_SECTIONS.OVERVIEW]: {
    [BUSINESS_TOOL_SUB_SECTIONS.OVERVIEW.COMPANY]: ICompanyOverviewForm;
    [BUSINESS_TOOL_SUB_SECTIONS.OVERVIEW.PROJECT]: IProjectOverviewForm;
    [BUSINESS_TOOL_SUB_SECTIONS.OVERVIEW.MARKET_CUSTOMERS]: IMarketAndCustomersOverviewForm;
    [BUSINESS_TOOL_SUB_SECTIONS.OVERVIEW.FINANCIAL_INFO]: IFinancialInfoOverviewForm;
    [BUSINESS_TOOL_SUB_SECTIONS.OVERVIEW.FUNDING_NEEDS]: IFundingNeedsOverviewForm;
  };

  [BUSINESS_TOOL_SECTIONS.EXECUTIVE_SUMMARY]: {
    [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.GOALS_AND_OBJECTIVES]?: IGoalsAndObjectivesExecutiveSummaryForm;
    [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.COMPANY_DESCRIPTION]: ICompanyDescriptionExecutiveSummaryForm;
    [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.PRODUCTS_OR_SERVICES]: IProductsOrServicesExecutiveSummaryForm;
    [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.MARKET_AND_CUSTOMERS]: IMarketAndCustomersExecutiveSummaryForm;
    [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.ORGANIZATION_AND_TEAM]: IOrganizationAndTeamExecutiveSummaryForm;
    [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.FINANCIAL_INFORMATION]: IFinancialInfoExecutiveSummaryForm;
    [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.FINANCING_NEEDS]: IFinancingNeedsExecutiveSummaryForm;
    [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.RISKS]: IRisksExecutiveSummaryForm;
  };

  [BUSINESS_TOOL_SECTIONS.ABOUT_COMPANY]: {
    [BUSINESS_TOOL_SUB_SECTIONS.ABOUT_COMPANY.GENERAL_INFO]: IGeneralAboutCompanyForm;
    [BUSINESS_TOOL_SUB_SECTIONS.ABOUT_COMPANY.COMPANY_HISTORY]: IHistoryAboutCompanyForm;
    [BUSINESS_TOOL_SUB_SECTIONS.ABOUT_COMPANY.MISSION_AND_STRATEGY]: IAboutCompanyMissionForm;
    [BUSINESS_TOOL_SUB_SECTIONS.ABOUT_COMPANY.SWOT]: ISwotAboutCompanyForm;
  };

  [BUSINESS_TOOL_SECTIONS.PRODUCTS_AND_SERVICES]: {
    [BUSINESS_TOOL_SUB_SECTIONS.PRODUCTS_AND_SERVICES.DESCRIPTION]: IProductAndServicesDescriptionForm;
    [BUSINESS_TOOL_SUB_SECTIONS.PRODUCTS_AND_SERVICES.ADVANTAGES]: IProductAndServicesAdvantagesForm;
    [BUSINESS_TOOL_SUB_SECTIONS.PRODUCTS_AND_SERVICES.R_AND_D]: IProductAndServicesRAndDForm;
    [BUSINESS_TOOL_SUB_SECTIONS.PRODUCTS_AND_SERVICES.DELIVERY_AND_SUPPORT]: IProductAndServicesDeliveryAndSupportForm;
  };

  [BUSINESS_TOOL_SECTIONS.MARKET_ANALYSIS]: {
    [BUSINESS_TOOL_SUB_SECTIONS.MARKET_ANALYSIS.MARKET_OVERVIEW]: IMarketAnalysisOverviewForm;
    [BUSINESS_TOOL_SUB_SECTIONS.MARKET_ANALYSIS.CONSUMER_NEEDS]: IConsumerNeeds;
    [BUSINESS_TOOL_SUB_SECTIONS.MARKET_ANALYSIS.COMPETITIVE_ANALYSIS]: ICompetitiveAnalysis;
  };

  [BUSINESS_TOOL_SECTIONS.MARKETING_AND_SALES]: {
    [BUSINESS_TOOL_SUB_SECTIONS.MARKETING_AND_SALES.SALES_PLAN]: ISales;
    [BUSINESS_TOOL_SUB_SECTIONS.MARKETING_AND_SALES.MARKETING_PLAN]: IMarketing;
  };

  [BUSINESS_TOOL_SECTIONS.BUSINESS_OPERATIONS]: {
    [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.LOCATION_AND_PREMISES]: ILocationsAndPremises;
    [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.EQUIPMENT]: IEquipment;
    [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.ASSETS]: IAssets;
    [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.TECHNOLOGICAL_PROCESS]: ITechnologicalProcess;
    [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.WORKING_RESOURCES]: IWorkingRecourses;
    [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.PARTNERS]: IPartners;
    [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.PRODUCTION]: IProduction;
    [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.QUALITY]: IQuality;
  };

  [BUSINESS_TOOL_SECTIONS.MANAGEMENT_AND_ORGANIZATION]: {
    [BUSINESS_TOOL_SUB_SECTIONS.MANAGEMENT_AND_ORGANIZATION.MANAGEMENT_TEAM]: IManagementAndOrganization;
    [BUSINESS_TOOL_SUB_SECTIONS.MANAGEMENT_AND_ORGANIZATION
      .EXTERNAL_PARTNERS_AND_ADVISERS]: IExternalPartnersAndAndAdvisers;
    [BUSINESS_TOOL_SUB_SECTIONS.MANAGEMENT_AND_ORGANIZATION.STAFF_ORGANIZATION]: IStaffOrganization;
  };

  [BUSINESS_TOOL_SECTIONS.IMPLEMENTATION_PLAN]: IImplementationPlan;
  [BUSINESS_TOOL_SECTIONS.RAISING_AND_ALLOCATING_FUNDS]: IRaisingAndAllocatingFunds;
  [BUSINESS_TOOL_SECTIONS.LEGAL_FRAMEWORKS_AND_RISKS]: ILegalFrameworkAndRisks;

  [BUSINESS_TOOL_SECTIONS.FINANCIAL_PLAN]: {
    [BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.KEY_ASSUMPTIONS]: IKeyAssumptions;
    [BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.REVENUE_PLAN]: IRevenuePlan;
    [BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.PLANNED_EXPENSES]: IPlannedExpenses;
    [BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.FINANCIAL_PERFORMANCE]: IFinancialPerformance;
    [BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.OPENING_BALANCE_SHEETS]: IOpeningBalanceSheets;
    [BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.PROFIT_AND_LOSS_PROJECTION]: IProfitAndLossProjections;
    [BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.PROJECTED_BALANCE_SHEETS]: IProjectedBalanceSheets;
    [BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.PROJECTED_CASH_FLOW]: IProjectedCashFlow;
    [BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.CASH_FLOW_ELEMENTS]: ICashFlowElements;
  };
};

export const salesAndPlannedProductsPeriodName = concatWithDot(
  BUSINESS_TOOL_SECTIONS.MARKETING_AND_SALES,
  BUSINESS_TOOL_SUB_SECTIONS.MARKETING_AND_SALES.SALES_PLAN,
  'period',
);

export const getInitialValues = (
  currentGlobalInitialValues: IGlobalSection = globalInitialValues,
): IBusinessToolForm => ({
  [BUSINESS_TOOL_SECTIONS.GLOBAL]: currentGlobalInitialValues,
  [BUSINESS_TOOL_SECTIONS.OVERVIEW]: {
    [BUSINESS_TOOL_SUB_SECTIONS.OVERVIEW.COMPANY]: overviewCompanyInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.OVERVIEW.PROJECT]: overviewProjectInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.OVERVIEW.MARKET_CUSTOMERS]: overviewMarketAndCustomersInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.OVERVIEW.FINANCIAL_INFO]: overviewFinancialInfoInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.OVERVIEW.FUNDING_NEEDS]: overviewFundingNeedsInitialValues,
  },

  [BUSINESS_TOOL_SECTIONS.EXECUTIVE_SUMMARY]: {
    [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.GOALS_AND_OBJECTIVES]:
      goalsAndObjectivesExecutiveSummaryInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.COMPANY_DESCRIPTION]: companyDescriptionExecutiveSummaryInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.PRODUCTS_OR_SERVICES]:
      productsOrServicesExecutiveSummaryInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.MARKET_AND_CUSTOMERS]:
      marketAndCustomersExecutiveSummaryInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.ORGANIZATION_AND_TEAM]:
      organizationAndTeamExecutiveSummaryInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.FINANCIAL_INFORMATION]: financialInfoExecutiveSummaryInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.FINANCING_NEEDS]: financingNeedsExecutiveSummaryInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.RISKS]: risksExecutiveSummaryInitialValues,
  },

  [BUSINESS_TOOL_SECTIONS.ABOUT_COMPANY]: {
    [BUSINESS_TOOL_SUB_SECTIONS.ABOUT_COMPANY.GENERAL_INFO]: generalAboutCompanyInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.ABOUT_COMPANY.COMPANY_HISTORY]: historyAboutCompanyInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.ABOUT_COMPANY.MISSION_AND_STRATEGY]: aboutCompanyMissionInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.ABOUT_COMPANY.SWOT]: swotAboutCompanyInitialValues,
  },

  [BUSINESS_TOOL_SECTIONS.PRODUCTS_AND_SERVICES]: {
    [BUSINESS_TOOL_SUB_SECTIONS.PRODUCTS_AND_SERVICES.DESCRIPTION]: productAndServicesDescriptionInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.PRODUCTS_AND_SERVICES.ADVANTAGES]: productAndServicesAdvantagesInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.PRODUCTS_AND_SERVICES.R_AND_D]: productAndServicesRAndDInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.PRODUCTS_AND_SERVICES.DELIVERY_AND_SUPPORT]:
      productAndServicesDeliveryAndSupportInitialValues,
  },

  [BUSINESS_TOOL_SECTIONS.LEGAL_FRAMEWORKS_AND_RISKS]: legalFrameworkAndRisksInitialValues,

  [BUSINESS_TOOL_SECTIONS.MANAGEMENT_AND_ORGANIZATION]: {
    [BUSINESS_TOOL_SUB_SECTIONS.MANAGEMENT_AND_ORGANIZATION.MANAGEMENT_TEAM]: managementAndOrganizationInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.MANAGEMENT_AND_ORGANIZATION.EXTERNAL_PARTNERS_AND_ADVISERS]:
      externalPartnersAndAndAdvisersInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.MANAGEMENT_AND_ORGANIZATION.STAFF_ORGANIZATION]: staffOrganizationInitialValues,
  },

  [BUSINESS_TOOL_SECTIONS.MARKET_ANALYSIS]: {
    [BUSINESS_TOOL_SUB_SECTIONS.MARKET_ANALYSIS.MARKET_OVERVIEW]: marketAnalysisOverviewInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.MARKET_ANALYSIS.CONSUMER_NEEDS]: consumerNeedsInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.MARKET_ANALYSIS.COMPETITIVE_ANALYSIS]: competitiveAnalysisInitialValues,
  },

  [BUSINESS_TOOL_SECTIONS.BUSINESS_OPERATIONS]: {
    [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.LOCATION_AND_PREMISES]: locationsAndPremisesInitialValue,
    [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.EQUIPMENT]: equipmentInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.ASSETS]: assetsInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.TECHNOLOGICAL_PROCESS]: technologicalProcessInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.WORKING_RESOURCES]: workingRecoursesInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.PARTNERS]: partnersInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.PRODUCTION]: productionInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.QUALITY]: qualityInitialValues,
  },

  [BUSINESS_TOOL_SECTIONS.MARKETING_AND_SALES]: {
    [BUSINESS_TOOL_SUB_SECTIONS.MARKETING_AND_SALES.SALES_PLAN]: salesInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.MARKETING_AND_SALES.MARKETING_PLAN]: marketingInitialValues,
  },

  [BUSINESS_TOOL_SECTIONS.RAISING_AND_ALLOCATING_FUNDS]: raisingAndAllocatingFundsInitialValues,

  [BUSINESS_TOOL_SECTIONS.IMPLEMENTATION_PLAN]: implementationPlanValues,

  [BUSINESS_TOOL_SECTIONS.FINANCIAL_PLAN]: {
    [BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.KEY_ASSUMPTIONS]: keyAssumptionsInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.REVENUE_PLAN]: revenuePlanInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.PLANNED_EXPENSES]: plannedExpensesInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.FINANCIAL_PERFORMANCE]: financialPerformanceInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.OPENING_BALANCE_SHEETS]: openingBalanceSheetsInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.PROFIT_AND_LOSS_PROJECTION]: profitAndLossProjectionInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.PROJECTED_BALANCE_SHEETS]: projectedBalanceSheetsInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.PROJECTED_CASH_FLOW]: projectedCashFlowInitialValues,
    [BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.CASH_FLOW_ELEMENTS]: cashFlowElementsInitialValues,
  },
});

export const useGetInitialValues = (): IBusinessToolForm => {
  const currentGlobalInitialValues = useGetGlobalInitialValues();
  const currentPlannedExpensesInitialValues = useGetPlannedExpensesInitialValues();

  return useMemo(
    () => ({
      ...getInitialValues(currentGlobalInitialValues),
      [BUSINESS_TOOL_SECTIONS.FINANCIAL_PLAN]: {
        ...getInitialValues(currentGlobalInitialValues)[BUSINESS_TOOL_SECTIONS.FINANCIAL_PLAN],
        [BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.PLANNED_EXPENSES]: currentPlannedExpensesInitialValues,
      },
    }),
    [currentGlobalInitialValues, currentPlannedExpensesInitialValues],
  );
};

export const initialValues = getInitialValues();
